/**
 * Shared layout wrapper for /privacy and /terms.
 * Provides consistent typography, spacing and a "back to home" footer link
 * matching the site's dark + gold luxury aesthetic.
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function LegalLayout({ eyebrow, title, effective, publisher, contact, website, children, testid }) {
  return (
    <div data-testid={testid} className="min-h-[70vh] bg-black text-neutral-200">
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] mb-3">{eyebrow}</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] text-white">{title}</h1>

        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4 text-[13px] text-neutral-300 space-y-1">
          <div><span className="text-neutral-500">Effective date:</span> {effective}</div>
          <div><span className="text-neutral-500">Publisher:</span> {publisher}</div>
          <div><span className="text-neutral-500">Contact:</span> <a href={`mailto:${contact}`} className="text-[#D4AF37] hover:underline">{contact}</a></div>
          <div><span className="text-neutral-500">Website:</span> <a href={website} className="text-[#D4AF37] hover:underline">{website}</a></div>
        </div>

        <article className="legal-prose mt-10">
          {children}
        </article>

        <div className="mt-16 pt-8 border-t border-white/10">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-400 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </div>

      {/* Scoped typography for legal content — matches site tone */}
      <style>{`
        .legal-prose h2 {
          font-family: var(--font-serif, "Cormorant Garamond", serif);
          color: #fff;
          font-size: 1.6rem;
          line-height: 1.2;
          margin-top: 2.5rem;
          margin-bottom: 0.85rem;
          letter-spacing: 0.01em;
        }
        .legal-prose h3 {
          color: #D4AF37;
          font-size: 0.72rem;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .legal-prose p { color: #cfcfcf; font-size: 0.95rem; line-height: 1.75; margin: 0.9rem 0; }
        .legal-prose strong { color: #fff; font-weight: 600; }
        .legal-prose ul { list-style: none; padding-left: 0; margin: 0.9rem 0; }
        .legal-prose ul > li {
          position: relative;
          padding-left: 1.25rem;
          margin: 0.4rem 0;
          color: #cfcfcf;
          font-size: 0.95rem;
          line-height: 1.7;
        }
        .legal-prose ul > li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.75rem;
          width: 6px;
          height: 6px;
          background: #D4AF37;
          border-radius: 999px;
        }
        .legal-prose a { color: #D4AF37; text-decoration: none; }
        .legal-prose a:hover { text-decoration: underline; }
        .legal-prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0 1.4rem;
          font-size: 0.9rem;
        }
        .legal-prose th, .legal-prose td {
          text-align: left;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .legal-prose th {
          color: #D4AF37;
          font-size: 0.65rem;
          text-transform: uppercase;
          letter-spacing: 0.22em;
          font-weight: 600;
          background: rgba(212,175,55,0.04);
        }
        .legal-prose td { color: #d0d0d0; }
        .legal-prose blockquote {
          border-left: 2px solid #D4AF37;
          padding-left: 1rem;
          color: #cfcfcf;
          font-style: italic;
          margin: 1.2rem 0;
        }
      `}</style>
    </div>
  );
}
